import { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';

import { useOnlineStatus } from '@/hooks/ui/useOnlineStatus';
import { appEvents } from '@/lib/app-events';

/**
 * Whether the offline strip should be up — ROUTE_INVENTORY Part 1, G6.
 *
 * Raised while the browser reports `navigator.onLine === false` OR when
 * api-client's `network-error` events suggest the upstream is unreachable even
 * though the browser thinks it is online.
 *
 * Why two signals?
 *   - `navigator.onLine` is authoritative when correct, but it sometimes lies
 *     (says online when a captive portal or a VPN hiccup means real fetches
 *     fail).
 *   - `network-error` events fire on every fetch that didn't reach a server.
 *     A burst of these (2+ within 10s) is a stronger negative signal than
 *     `navigator.onLine` alone.
 *
 * On reconnect it calls `queryClient.invalidateQueries()` so cached-but-stale
 * data refreshes (TanStack Query's `refetchOnReconnect` covers ACTIVE queries;
 * this also covers the ones the user is about to visit).
 *
 * Not dismissible: it clears when the connection does. Suppressed on `/auth`,
 * whose form gives its own connectivity feedback — `classifyAuthError` turns
 * a failed auth fetch into "You appear to be offline..." in the form's error
 * box, and Supabase auth calls never emit `network-error` anyway.
 */

// A single failed request happens normally - only a burst is suspicious.
const NETWORK_ERROR_THRESHOLD = 2;
const NETWORK_ERROR_WINDOW_MS = 10_000;
// Clear a suspicion after this long with no new network-error.
const SUSPECTED_OFFLINE_CLEAR_MS = 15_000;

export function useOfflineBanner(): { raised: boolean } {
  const { isOnline } = useOnlineStatus();
  const queryClient = useQueryClient();
  const wasOffline = useRef(false);
  const location = useLocation();
  const [suspectedOffline, setSuspectedOffline] = useState(false);
  const networkErrors = useRef<number[]>([]);
  const clearTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (!isOnline) {
      wasOffline.current = true;
      return;
    }
    if (wasOffline.current) {
      wasOffline.current = false;
      queryClient.invalidateQueries();
      // The offline→online EDGE clears a suspicion too. It used to be its own
      // effect checking `isOnline && suspectedOffline` - a LEVEL, true the
      // instant a burst raised the flag with the browser still claiming
      // online, i.e. in exactly the case the burst detector exists for - so
      // the strip was cleared in the same commit it was raised.
      setSuspectedOffline(false);
      networkErrors.current = [];
      if (clearTimer.current) clearTimeout(clearTimer.current);
    }
  }, [isOnline, queryClient]);

  useEffect(() => {
    const off = appEvents.on('network-error', () => {
      const now = Date.now();
      networkErrors.current = networkErrors.current.filter(
        t => now - t < NETWORK_ERROR_WINDOW_MS
      );
      networkErrors.current.push(now);

      if (networkErrors.current.length >= NETWORK_ERROR_THRESHOLD) {
        setSuspectedOffline(true);
        if (clearTimer.current) clearTimeout(clearTimer.current);
        clearTimer.current = setTimeout(() => {
          setSuspectedOffline(false);
          networkErrors.current = [];
        }, SUSPECTED_OFFLINE_CLEAR_MS);
      }
    });
    return () => {
      off();
      if (clearTimer.current) clearTimeout(clearTimer.current);
    };
  }, []);

  const raised =
    !location.pathname.startsWith('/auth') && (!isOnline || suspectedOffline);
  return { raised };
}
